import { PostHogProvider as _PostHogProvider } from 'posthog-js/react';
import React from 'react';

import {
    getRunEnvironment,
    getRuntimeConfig,
} from '@apps/web/lib/runtime_config';

// Free-text query params that can carry typed person names (e.g. the
// Coverage page’s caregiver search). Auto-captured events include URLs
// ($current_url, $referrer, ...), so these values must be redacted before
// anything leaves the browser.
const REDACTED_URL_QUERY_PARAMS = ['search', 'query'];

function scrubUrlQueryParams(value: unknown): unknown {
    if (typeof value !== 'string' || !value.includes('?')) {
        return value;
    }
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return value;
    }
    let changed = false;
    for (const param of REDACTED_URL_QUERY_PARAMS) {
        if (url.searchParams.has(param)) {
            url.searchParams.set(param, 'redacted');
            changed = true;
        }
    }
    return changed ? url.toString() : value;
}

export function sanitizePosthogProperties(
    properties: Record<string, unknown>,
): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [
            key,
            scrubUrlQueryParams(value),
        ]),
    );
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
    const isDevelopment = getRunEnvironment() === 'development';

    return (
        <_PostHogProvider
            apiKey={getRuntimeConfig().posthogKey ?? ''}
            options={{
                api_host: 'https://us.posthog.com',
                loaded: (posthog) => {
                    if (isDevelopment) {
                        posthog.debug();
                    }
                },
                opt_out_capturing_by_default: isDevelopment,
                sanitize_properties: sanitizePosthogProperties,
            }}
        >
            {children}
        </_PostHogProvider>
    );
}
